import Decimal from 'decimal.js';

import {RunConfigService, storage, events, nuskin, UrlService, PersonalOfferStorageService,
    StringService, NsNotQualifiedException, NsQuantityNotAvailable, NsProductNotReleasedForSale, equinoxLocalStorage} from '@nuskin/ns-util';
import {AdrService, Product, ScanCard, CurrencyService} from '@nuskin/ns-shop';
import {UserService, AccountManager}  from '@nuskin/ns-account';
import ProductService from './productService';
import Cart from './cart.js';
import AdrEditService from '../adr/AdrEditService.js';
import QualificationService from '../qualification/qualificationService';
import {ProductStatus} from '@nuskin/ns-product-lib';
import webLoyalty from '@nuskin/ns-loyalty-web';
import axios from 'axios';
import { getCachedConfigField, getCachedConfiguration, getConfiguration } from '@nuskin/configuration-sdk';
import equinoxCartService from './equinoxCartService';
import {retrieveTogglesBasedOnEnvironment} from '@nuskin/ns-feature-flags';

export default {
    goToCartPage,
    getCartPage,
    goToCheckoutPage,
    getCheckoutPage,
    setCartProperties,
    updateItemPrices,
    checkForMissingEventNames,
    getCartProperty,
    syncPersistedInfo,
    clearCart,
    getCartInfo,
    getPriceTypeTotal,
    getItemData,
    getFirstItemBySku,
    getItemTitleBySku,
    hasItems,
    hasEventItems,
    hasPreviewItems,
    getItemCnt,
    addProductToCart,
    addSkuToCart,
    getAddToCartQty,
    changeItemQty,
    syncCartItemToSapItem,
    checkItemAvailability,
    removeItemBySku,
    removeItemByKey,
    removeItems,
    toggleItemAdrStatus,
    getOrderSkuQueryParams,
    isBusinessPortfolioAdr,
    setBusinessPortfolioAdr,
    setItemAdrFlags,
    changeBpItem,
    cloneCart,
    adrHasAgelocMe,
    setSignupMode,
    inSignupMode,
    minAdrPsvMet,
    checkProductLanguages,
    scanQualifiedCnt,
    getScanCards,
    removeScanCard,
    hasScanCardNumber,
    addScanCard,
    updateItemPointsPrice,
    resetPointsAllocations,
    getItemUnitPsv,
    setCartString,
    toggleLoyaltyRewardPoints,
    getLoyaltyMaxRedeemAmount,
    updateLoyaltyPoints,
    toggleLoyaltyRewardPointsAdr,
    calculateLoyaltyEarnings,
    calculateLoyaltyOrderTotal,
    calculateLoyaltyEarningsToNextIncrement,
    getOrderLoyaltyRewardsPointsJson,
    clearLoyaltyPoints,
    loadLoyaltyPoints,
    containsAdrItems,
    checkIsMySiteSiteEnable
}

const CART_STORAGE_KEY = storage.metadata.ECOMM3_CART;
const INVALID_PROP_TYPE_QUERIES = ['object', 'function'];

let _cart_;
let activeEvents = [];
let cartSynced = false;

const awaitForConfig = async () => {
    await getConfiguration(['Cart', 'Checkout']);
};

let flagsPromise = null
let featureFlags = false
const checkFlag = async (flag) => {
    if (!featureFlags && !flagsPromise) {
        flagsPromise = retrieveTogglesBasedOnEnvironment().then(flagsFromServer => featureFlags = flagsFromServer)
    }

    if (!featureFlags) {
        await flagsPromise
    }

    return featureFlags.has(flag)
}
checkFlag()

/**
 *
 * @memberof CartService
 * @example
 * CartService.getCart()
 * The cart will be cleared if runConfig.country does not match the one in the stored cart.
 * The cart will be cleared if the logged in goes from something to nothing (logout)
 * The cart will be cleared if the cart is a pitch cart and the user is not in pitch mode, or vice versa
 * The cart will not be cleared if the logged in goes from nothing to something (login)
 *
 * @returns nuskin.newShop.Cart
 * @private
 */
function _getCart() {
    if (!_cart_) {
        let runConfig = RunConfigService.getRunConfig(null),
            user = UserService.getUser(),
            userId = user ? user.id : null,
            cartJson = storage.getItem(CART_STORAGE_KEY),
            anonymousCartId = storage.getItem('anonymousCartId'),
            isPitchApp = PersonalOfferStorageService.isPersonalOffer();

        if (cartJson && !cartJson.cntryCd) {
            cartJson.cntryCd = runConfig.country;
        }

        // If the cartJson userId is empty OR null, then we are going from nothing to something, and we want to keep our items
        if (cartJson &&
            (!UrlService.isLocalePage() || (cartJson.cntryCd === runConfig.country)) &&
            (!cartJson.userId || cartJson.userId === userId) &&
            (!(isPitchApp ^ cartJson.isPitchCart))) {

            if (!userId) {
                if (!anonymousCartId || anonymousCartId != cartJson.id) {
                    cartJson.id = '';
                    cartJson.version = -1;
                    cartJson.userId = '';
                    cartJson.products.forEach((item) => item.id = '');
                }
            }

            if (!cartJson.userId && userId != '') {
                cartJson.userId = userId;
                storage.setItem(CART_STORAGE_KEY, cartJson);
            }
            _cart_ = new Cart(cartJson);
            let modified = _cart_.updateLineItemPrices(activeEvents);
            if (_cart_.setBusinessPortfolioAdr(false) || modified) {
                setCart(_cart_);
            }
        } else {
            _cart_ = new Cart({cntryCd: runConfig.country, userId: userId, isPitchCart: isPitchApp});
            setCart(_cart_);
        }
    }

    return _cart_;
}

/**
 * This writes the good product to local storage (Non SAP I guess). The product key looks something like "01160101|O|N||".
 * toJSON - returns the cart in JSON format
 * @memberof CartService
 * @returns {object}
 * @private
 */
function setCart(cart, updateTimestamp = true) {
    if (cart != null && cart instanceof Cart) {
        cart.timestamp = updateTimestamp ? Date.now() : cart.timestamp;
        storage.setItem(CART_STORAGE_KEY, cart.toJSON());
    } else {
        if (cart) {
            cart.timestamp = updateTimestamp ? Date.now() : cart.timestamp;
            storage.setItem(CART_STORAGE_KEY, cart);
        } else {
            cart = new Cart({cntryCd: RunConfigService.getRunConfig().country, userId: '', type: _cart_.type});
            storage.setItem(CART_STORAGE_KEY, cart);
        }
    }
    _cart_ = cart;
}

/**
 * Clears the cart from localStorage.
 *
 * @return {*}
 */
function clearCart(removePersistedCart = true) {
    _getCart().removeItems({all: true, removeCart: removePersistedCart});  // to trigger events
    setCart(null);
    return _cart_;
}

function setCartProperties(propsJson = {}, updateTimestamp = false) {
    let cart = _getCart();

    Object.keys(propsJson).forEach(key => {
        if (cart.hasOwnProperty(key) &&
            typeof propsJson[key] === typeof cart[key] &&
            !INVALID_PROP_TYPE_QUERIES.find(invalid => typeof cart[key] === invalid)) {

            if (cart[key] !== propsJson[key]) {
                cart[key] = propsJson[key];
            }
        } else {
            console.log(`Could not set property: ${key}`);
        }
    });

    setCart(cart, updateTimestamp);
}

function updateItemPrices() {
    let retVal = false,
        cart = _getCart();

    if (cart.updateLineItemPrices(activeEvents)) {
        setCart(cart);
        retVal = true;
    }
    return retVal;
}

async function checkForMissingEventNames(order) {
    await awaitForConfig();
    const cart = _getCart();
    await cart.setMissingEventNames(order);
    setCart(cart);
}

function getCartProperty(key) {
    let cart = _getCart(),
        retVal;

    if (cart.hasOwnProperty(key) && !INVALID_PROP_TYPE_QUERIES.find(invalid => typeof cart[key] === invalid)) {
        retVal = cart[key];
    } else {
        console.log(`Could not get property: ${key}`);
    }
    return retVal;
}

function getItemData(options) {
    return _getCart().getItemData(options);
}

/**
 *  Returns the first item found in the cart based on sku and options
 *
 *  @param sku
 *  @param options
 *  @return {*}
 */
function getFirstItemBySku(sku, options) {
    return _getCart().getFirstItemBySku(sku, options);
}

/**
 * Given a SKU, will search the cart based on optinos and return the item's title
 *
 * @param sku
 * @param options
 * @return string
 */
function getItemTitleBySku(sku, options) {
    return _getCart().getItemTitleBySku(sku, options);
}

function hasItems(options) {
    return _getCart().hasItems(options);
}

function hasEventItems() {
    return _getCart().hasEventItems();
}

function hasPreviewItems() {
    return _getCart().hasPreviewItems();
}

function getItemCnt(options) {
    return _getCart().getItemCnt(options);
}

function getCartInfo(options) {
    return _getCart().getCartInfo(options);
}

function getPriceTypeTotal(priceType) {
    return _getCart().getItems({cartOrderItems: true}).map(item => {
        return item.product.getPricing(priceType) * item.qty;
    }).reduce((a, b) => a + b, 0);
}

/**
 * Checks status and order types.
 *
 * @param {*} product
 * @returns
 */
function itemIsAvailable(product) {
    return product.status === ProductStatus.ReleasedForSale &&
        (product.hasOrderOption() || product.hasAdrOption());
}

/**
 * Adds a product to the cart. If is not a SAP product it checks for excusivity before
 *  adding to the cart.
 *
 * @param options - {
 *              userId: id of logged in user or '',
 *              cntryCd: country code of market,
 *              product: nuskin.newShop.Product,
 *              qty: number of products,
 *              maxQty: maximum qty,
 *              adr: true === product is for adr,
 *              isBusinessPortfolio: true = product is designated as business portfolio,
 *              canDelete: optional, default is true
 *              redeem: default is false
 *              referrer[optional]: contains the referrer that called this function, or in other words, where the product was
 *                  added from (search, productPage, categoryPage, etc.)
 *          }
 * @returns {Promise<void>}
 * @private
 */
async function addProductToCart(options) {
    await awaitForConfig();
    let cart = _getCart();

    if (!options.sapLineId && !itemIsAvailable(options.product)) {
        throw new NsProductNotReleasedForSale('This product is not released for sale');
    }

    if (typeof options.qty != 'number') {
        options.qty = parseInt(options.qty);
    }

    if (!options.sapLineId && options.product.isExclusive) {
        let item = cart.getItemData().find(cartItem => cartItem.sku === options.product.sku),
            origQty = item ? item.qty : 0;

        if (cartSynced) {
            await _checkQualification(options.product.sku, options.qty + origQty);
        } else {
            await _adjustToQualificationQuantity(options, origQty)
        }
    }

    const language = options.product.lang || options.product.language;
    options.product.lang = language;
    options.product.language = language;

    let itemKey = cart.addProduct({
        id: options.id,
        product: options.product,
        qty: options.qty,
        maxQty: options.maxQty,
        adr: options.adr,
        isBusinessPortfolio: options.isBusinessPortfolio,
        canDelete: options.canDelete,
        redeem: options.redeem,
        referrer: options.referrer,
        sapLineId: options.sapLineId,
        qtyRedeemWithPoints: options.qtyRedeemWithPoints,
        loyaltyPointsUsed: options.loyaltyPointsUsed,
        replace: options.replace,
        activeEvents: activeEvents
    });

    const item = itemKey ? cart.getItemByKey(itemKey) : null;
    setCart(cart,  item ? !item.isSapLineItem() : true);
    return itemKey;
}

/**
 * @memberof CartService
 * @example
 * CartService.addSkuToCart(options, callbackFn)
 * options: {
 *      sku: sku to be added to the CART
 *      userId: userId of logged in user
 *      cntryCd: countryCode of market
 *      language: languageCode
 *      qty: quantity
 *      region: for CA
 *      isAdr[optional]: a boolean to indicate if the product isAdr
 *      referrer[optional]: a string indicating where the product was added (search, productPage, categoryPage, etc.)
 *  }
 */
async function addSkuToCart(options) {
    let retVal,
        response = await ProductService.getProductBySku(options),
        product = response.success && response.product;

    if (product &&
        ((options.isAdr === null || options.isAdr === undefined) ||
         (options.isAdr === true && product.hasAdrOption()) ||
         (options.isAdr === false && product.hasOrderOption()))) {
        let isAdr = (product.isAdrOnly() || options.isAdr) && !AdrService.isAdrOverride(),
            qty = options.qty || 1,
            maxQty = options.maxQty || 999,
            isPitchCart;

        if (options.adrOnly) {
            product.orderTypes.order = false;
        }
        if (options.isPitchCart) {
            isPitchCart = options.isPitchCart;
        }

        product.lang = options.language;

        // if agelocme, override certain data
        if (options.agelocme && options.agelocme.isCustom) {
            product.agelocme = options.agelocme;
        }

        // Setup the parameters.
        let itemKey = await addProductToCart({
            id: options.id,
            userId: options.userId,
            cntryCd: options.cntryCd,
            product: product,
            qty: qty,
            maxQty: maxQty,
            adr: isAdr,
            canDelete: options.canDelete,
            isBusinessPortfolio: options.isBusinessPortfolio,
            redeem: options.redeem,
            isPitchCart: isPitchCart,
            referrer: options.referrer
        });

        retVal = {
            success: true,
            sku: product.sku,
            isAdr: isAdr,
            isBusinessPortfolio: options.isBusinessPortfolio,
            itemKey: itemKey,
            product: product,
            options: options
        };

    } else {
        retVal = response;
    }

    return retVal;
}

async function getBaseSkuCartQuantity(baseSku) {
    await awaitForConfig();
    const cartContents = await QualificationService.convertCartDataToBaseSkus(_getCart().getItemData(), true) || {}
    const baseSkuData = cartContents[baseSku] || {}
    const quantity = baseSkuData.qty || 0
    return quantity
}

/**
 * Returns the quantity that is available for this SKU to be added to the cart. This
 * takes into account qualification quantity and quatity already in the cart.
 *
 * @param product - Product object or SKU string
 * @returns retVal  available quantity to add to cart
 */
async function getAddToCartQty(product) {
    let retVal = 0;

    // Make sure the needed config is loaded.
    await awaitForConfig();

    if (typeof product === 'string') {
        const response = await ProductService.getProductBySku({sku: product});
        product = response.success && response.product;
    }
    if (product instanceof Product) {
        let cartItem = _getCart().getItemData().find(cartItem => cartItem.sku === product.sku),
            cartQty = cartItem ? cartItem.qty : 0;

        retVal = product.maxQuantity - cartQty;
        if (product.isExclusive) {
            try {
                const qual = await QualificationService.getQualification(product.sku)
                const productIdentifier = qual.productId || qual.sku;
                cartQty = await getBaseSkuCartQuantity(productIdentifier)
                retVal = product.maxQuantity - cartQty;
                let qualQty = qual && qual.isConsumable && qual.quantity - cartQty > 0
                    ?
                    qual.quantity - cartQty
                    :
                    0;

                if (qualQty < retVal) {
                    retVal = qualQty;
                }
            } catch (err) {
                retVal = 0;
            }
        }
    }

    return retVal;
}

async function changeItemQty(cartItemInfo, qty = 0, referror) {
    await awaitForConfig();
    if (qty === 0) {
        // Remove the Product
        removeItemByKey(cartItemInfo.key);
    } else {
        let cart = _getCart();

        if (cartItemInfo.isExclusive) {
            await _checkQualification(cartItemInfo.sku, qty);
        }

        // Update the Quantity
        if (cart.setItemQuantity(cartItemInfo.key, qty, referror)) {
            cart.trimScanCards();
            setCart(cart);
        } else {
            throw new NsQuantityNotAvailable('');
        }
    }
}

function removeItemBySku(sku) {
    if (sku.length == '8') {
        let cart = _getCart();
        let cartItems = cart.getItems({all: true});
        for (let i = 0; i < cartItems.length; i++) {
            if (cartItems[i].product.sku === sku) {
                cart.removeProduct(cartItems[i].key);
                break;
            }
        }
        cart.trimScanCards();
        setCart(cart);
    }
}

function removeItemByKey(itemKey) {
    let cart = _getCart();

    cart.removeProduct(itemKey);
    cart.trimScanCards();
    setCart(cart);
}

function removeItems(options = {all: true}) {
    _getCart().removeItems(options);
}

function toggleItemAdrStatus(itemKey, syncAll) {
    let cart = _getCart(),
        makeAdr = cart.toggleToAdr(itemKey, activeEvents);

    if (syncAll) {
        setItemAdrFlags(makeAdr);
    } else {
        cart.trimScanCards();
        setCart(cart);
    }
}

function getOrderSkuQueryParams(queryParamsExist) {
    return _getCart().getOrderSkuQueryParams(queryParamsExist);
}

function isBusinessPortfolioAdr() {
    return _getCart().isBusinessPortfolioAdr();
}

function setBusinessPortfolioAdr(adrVal) {
    return _getCart().setBusinessPortfolioAdr(adrVal);
}

function setItemAdrFlags(makeAdr) {
    let cart = _getCart();

    cart.getItems({cartItems: true}).forEach(cartItem => {
        if (cartItem.adr != makeAdr && cartItem.canToggleAdr()) {
            cart.toggleToAdr(cartItem.key);
        }
    });
    cart.trimScanCards();
    setCart(cart);
}

function changeBpItem(altBpSku) {
    let cart = _getCart();

    cart.replaceBpWithAltBp(altBpSku);
    setCart(cart);
}

function cloneCart(cart) {
    // Clone the cart, so that we are not modifying the original
    return new Cart(cart.toJSON());
}

function adrHasAgelocMe() {
    let items = _getCart().getItems({cartItems: true});
    for (let i = 0; i < items.length; i++) {
        if (items[i].isAgelocme() && items[i].adr) {
            return true;
        }
    }
    return false;
}

function setSignupMode() {
    let runConfig = RunConfigService.getRunConfig(null),
        user = UserService.getUser();

    // check for signup mode
    if (runConfig.noSignupMode !== true && user && AccountManager.isLoggedIn() != null) {
        if (_getCart().checkForSignup) {
            if (user.countryCd == runConfig.country) {
                _getUserAddedItemSkus(user.id, user.eid, runConfig.country);
            }
        }
    }
}

function inSignupMode() {
    let cart = _getCart();

    return cart ? cart.inSignup : false;
}

function minAdrPsvMet() {
    let retVal = true,
        cart = _getCart(),
        // ADR one-time ship-immediate order uses ADR Override mode for UI behavior,
        // but is treated like standard order for minimum PSV requirements
        cartItemTypes = (AdrService.isAdrOverride() && !AdrService.isAdrOneTimeOrder()) ? {cartItems: true} : {cartAdrItems: true},
        cartPsv = AdrService.isAdrOverride() ? cart.totalOrderPsv : cart.totalAdrPsv;

    if (cart.hasItems(cartItemTypes)) {
        retVal = getCachedConfigField('minAdrPsv') <= cartPsv ||
            _haveOverrideSku(cart.getItems(cartItemTypes), getCachedConfigField('minPsvAdrOverrideSkus'));
    }
    return retVal;
}

function checkProductLanguages() {
    let runConfig = RunConfigService.getRunConfig(null),
        options = {
            cntryCd: runConfig.country,
            language: runConfig.language
        };

    _getCart().getItems({cartItems: true}).forEach(_cartItem => {
        let cartItem = _cartItem;

        if (runConfig.language !== cartItem.product.lang) {
            options.sku = cartItem.product.sku;
            options.priceType = cartItem.product.priceType;
            ProductService.getProductBySku(options)
                .then((response) => {
                    if (response.success && response.product) {
                        let cart = _getCart();

                        cart.changeProduct(cartItem.key, response.product);
                        setCart(cart);
                    }
                });
        }
    });
}

function scanQualifiedCnt() {
    return _getCart().scanQualifiedCnt();
}

function getScanCards() {
    return _getCart().scanCards;
}

function removeScanCard(cardNumber) {
    let cart = _getCart();

    cart.removeScanCard(cardNumber);
    setCart(cart);
    return getScanCards();
}

function hasScanCardNumber(cardNumber) {
    return _getCart().hasScanCard(cardNumber);
}

function addScanCard(scanCard) {
    if (scanCard instanceof ScanCard) {
        let cart = _getCart();

        cart.addScanCard(scanCard);
        setCart(cart);
    }
}


function updateItemPointsPrice(sapItemKey, orderItemKey, qtyRedeem) {
    let cart = _getCart(),
        sapCartItem = cart.getItemByKey(sapItemKey),
        orderCartItem = cart.getItemByKey(orderItemKey),
        deltaItemTotal = 0;

    if (sapCartItem) {
        let itemPrice = sapCartItem.getQty() === qtyRedeem ? 0 : orderCartItem.product.getPrice(),
            itemTotal = itemPrice * (sapCartItem.qty - qtyRedeem),
            oldItemTotal;

        // adjustSapItemPv(sapItem, qtyRedeem);
        sapCartItem.product.pv = getItemUnitPsv(sapItemKey) * (sapCartItem.getQty() - qtyRedeem);
        sapCartItem.qtyRedeemWithPoints = qtyRedeem;

        oldItemTotal = sapCartItem.product.getPrice() || 0;  // price might be undefined
        deltaItemTotal = itemTotal - oldItemTotal;

        sapCartItem.product.setPrice(itemTotal);
        sapCartItem.redeem = qtyRedeem > 0 ? true : false;
        cart.addCartItem(sapCartItem, true);
        setCart(cart);
        syncCartItemToSapItem(sapItemKey, orderItemKey);
    }
    return {deltaItemTotal, sapItemData: sapCartItem.toFlatJson()};
}

function resetPointsAllocations() {

}

function getItemUnitPsv(sapItemKey) {
    let cart = _getCart(),
        sapItem = cart.getItemByKey(sapItemKey),
        unitPsv = 0;

    if (sapItem.product.pv > 0) {
        // calculate based off of simulate. Should not be a divide by 0 since lineItem price > 0. (qty > qtyRedeemWithPoints
        unitPsv = sapItem.product.pv / (sapItem.qty - sapItem.qtyRedeemWithPoints);
    } else {
        // can't base it off of simulate, use cart unit price.
        cart.getItems({cartItems: true})
            .filter(cartItem => cartItem.getSku() === sapItem.getSku())
            .forEach(cartItem => {
                unitPsv = cartItem.getSinglePv();
            });
    }

    return unitPsv;
}

function setCartString(adrOrder, shipImmediate) {
    _getCart().setCartString(adrOrder, shipImmediate);
}

/**
 * This will sync a cartItem from an sapItem.  Currently only a couple of
 *  redeem fields are synced.
 *
 * @param sapItemKey
 * @param cartItemKey
 */
function syncCartItemToSapItem(sapItemKey, cartItemKey = null) {
    let cart = _getCart(),
        sapItem = cart.getItemByKey(sapItemKey),
        cartItem = cartItemKey ? cart.getItemByKey(cartItemKey) : null;

    if (sapItem) {
        if (!cartItem) {
            cart.getItems({cartItems: true})
                .filter(cartItem => cartItem.getSku() === sapItem.getSku())
                .forEach(_cartItem => {
                    cartItem = _cartItem;
                });
        }
        cartItem.redeem = sapItem.redeem;
        cartItem.qtyRedeemWithPoints = sapItem.qtyRedeemWithPoints;
        cartItem.product.subPriceFlag = sapItem.product.subPriceFlag;
        cart.addCartItem(cartItem, true);
        setCart(cart);
    }
}

/**
 * This check a cart for qualified items and verifies that the
 * quantity is at or below qualified amounts. If the quantity in
 * the cart is too high an adjustment is made.
 */
async function checkItemAvailability() {
    await awaitForConfig();
    const cart = _getCart();

    for (const item of cart.getItems()) {
        if (!itemIsAvailable(item.product)) {
            cart.removeProduct(item.key);
        } else if (item.product.isExclusive) {
            // get qualifications for product
            const response = await ProductService.getProductBySku({sku: item.product.sku});
            const productInfo = response.success && response.product;
            let qual = null
            if (productInfo.equinoxProductId) {
                qual = await QualificationService.getQualification(productInfo.equinoxProductId)
            }

            if (!qual){
                qual = await QualificationService.getQualification(productInfo.sku)
            }
            let cartQty = 0;
            // if base sku
            if (productInfo.baseSku) {
                cartQty = await getBaseSkuCartQuantity(productInfo.baseSku)
            }
            const qualCnt = qual ? qual.quantity : 0;

            // If they have more in their cart than qualifications remove the item
            if ((qualCnt - cartQty) < 0) {
                cart.removeProduct(item.key);
            } else if (qualCnt < item.qty) {
                await changeItemQty(item.toFlatJson(), qualCnt, 'qualification check');
            }
        }
    }
}

// ------------------------------------------------------------------------------------------------
//
//        Define all private methods.
//
// ------------------------------------------------------------------------------------------------

/**
 * This presumes the sku in question is an exclusive sku
 *
 * @param sku
 * @param newQty
 * @returns {Promise<void>}
 * @private
 */
async function _checkQualification(sku, newQty) {
    let isQualified = await QualificationService.isQualified(sku, newQty, _getCart().getItemData());

    if (!isQualified) {
        let str = 'You are not qualified to purchase this product';
        try {
            str = await StringService.getString('exclusiveOfferMessage', 'You are not qualified to purchase this product')
        } catch (e) {
            console.log(e);
        }
        throw new NsNotQualifiedException(str);
    }
}

async function _adjustToQualificationQuantity(options, origQty) {
    const qualQty = await QualificationService.getQualifiedQty(options.product.sku);

    if (qualQty < origQty + options.qty) {
        if (qualQty < origQty) {
            options.qty = origQty - qualQty;
        } else {
            options.qty = qualQty - origQty;
        }
    }
}

function _haveOverrideSku(adrCartItems, overrideSkus) {
    let hasOverrideSku = false;

    if (typeof overrideSkus !== "undefined") {
        if (typeof overrideSkus === 'string') {
            overrideSkus = [overrideSkus];
        }
        for (let j = 0; j < adrCartItems.length; j++) {
            if (overrideSkus.indexOf(adrCartItems[j].getSku()) != -1) {
                hasOverrideSku = true;
            } else {
                hasOverrideSku = false;
            }
        }
    }
    return hasOverrideSku;
}

function _getUserAddedItemSkus(userId, eid, cntryCd) {

    const onReceivedActiveEvents = activeEvents => {
        /*
        The active events list is null until it has been initialized.
        Proceed with the callback code only once the list is initialized.
         */
        if (!(activeEvents instanceof Array)) {
            return;
        }

        /*
        Unsubscribe to prevent this callback from executing more than once
        after valid data has been received.
         */
        events.unsubscribe(events.salesevent.INITIAL_ACTIVE_EVENTS, onReceivedActiveEvents);

        if (getCachedConfigField('disableSalesEventSignupCheck') && activeEvents.length > 0) {
            /*
            If in an active event, skip the signup requirements network
            call and proceed as though signup requirements have been met.
             */
            _addedSkusCallbackFn({
                userId: userId,
                cntryCd: cntryCd,
                isSignup: false,
                sku: null,
                otherSkus: null
            });
        } else {
            let runConfig = RunConfigService.getRunConfig(),
                url = getCachedConfigField('signupRequirementServiceUrl').replace('{sapId}', userId).replace('{countryCd}', cntryCd)
                    .replace('{homeMarket}', cntryCd);
            // check to see if business portfolio needs to be added

            axios({
                method: 'GET',
                url,
                headers: {
                    'Authorization': `JWT ${eid}`,
                    'client_id': runConfig.clientId,
                    'client_secret': runConfig.clientSecret
                },
                data: JSON.stringify({eid: eid})
            }).then(_signupReqMetCallbackFn(userId, cntryCd, _addedSkusCallbackFn))
                .catch(() => console.log('SIGNUP CHECK ERROR'));
        }
    };

    /*
    Retrieve the list of active sales events. Subscribe to changes to this
    list to avoid a potential race condition where list is retrieved before
    it has been initialized.
     */
    events.getValue(events.salesevent.INITIAL_ACTIVE_EVENTS, onReceivedActiveEvents, true);
}

function _signupReqMetCallbackFn(_userId, _country, _callbackFn) {
    let userId = _userId,
        country = _country,
        callbackFn = _callbackFn;
    return function(response = {}) {
        const signupRequirements = response.data && response.data.SignupRequirements;
        const requirementsMet = !!(signupRequirements && signupRequirements.requirementsMet);
        const signupSkus = signupRequirements && signupRequirements.signupSkus;
        let sku = null;
        let otherSkus = null;

        if (!requirementsMet && signupSkus && signupSkus.length > 0) {
            sku = signupSkus[0];
            otherSkus = signupSkus.splice(1);
        }
        callbackFn({
            userId: userId,
            cntryCd: country,
            isSignup: !requirementsMet,
            sku: sku,
            otherSkus: otherSkus
        });
    };
}

function _addedSkusCallbackFn(response) {
    let runConfig = RunConfigService.getRunConfig(null),
        user = UserService.getUser(),
        cart = _getCart();

    cart.inSignup = response.isSignup;
    cart.checkForSignup = false;
    setCart(cart);
    if (response.sku) {
        if (response.isSignup) {
            let options = {
                isAdr: false,
                isBusinessPortfolio: true,
                userId: response.userId,
                sku: response.sku,
                otherSkus: response.otherSkus,
                cntryCd: response.cntryCd,
                language: runConfig.language,
                region: _getUserRegion(user)
            };

            ProductService.getProductBySku(options)
                .then((result) => {
                    if (result.success && result.product) {
                        let product = result.product;

                        addProductToCart({
                            userId: options.userId,
                            cntryCd: options.cntryCd,
                            product: product,
                            qty: 1,
                            maxQty: 999,
                            adr: false,
                            isBusinessPortfolio: true
                        }).then(() => {
                            if (options.otherSkus.length > 0) {
                                for (let i = 0; i < options.otherSkus.length; i++) {
                                    ProductService.getProductBySku({
                                        sku: options.otherSkus[i],
                                        cntryCd: options.cntryCd,
                                        language: options.language
                                    }).then((result) => {
                                        let cart;

                                        if (result.sucess && result.product) {
                                            cart = _getCart();
                                            cart.addAlternateBusinessPortfolio(result.product);
                                            setCart(cart);
                                        }
                                    });
                                }
                            }
                        });
                    }
                });
        }
    }
}

function _getUserRegion(user) {
    if (user && user.shipTo && user.shipTo.country === 'CA') {
        return user.shipTo.region;
    }
    return '';
}

function clearLoyaltyPoints() {
    let cart = _getCart();
    let cartUpdateObject = {};
    cartUpdateObject = {
        loyaltyRewards: false,
        loyaltyRewardPoints: 0.0,
        loyaltyAdrRewards: false,
        loyaltyAdrRewardPoints: 0.0,
        loyaltyTotalPoints: 0
    };

    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);

}

function toggleLoyaltyRewardPoints() {
    let cart = _getCart();
    let cartUpdateObject = {};

    if (cart.loyaltyRewards == true) {
        cartUpdateObject = {
            loyaltyRewards: false,
            loyaltyRewardPoints: 0.0 };
    } else {
        cartUpdateObject = {
            loyaltyRewards: true,
            loyaltyRewardPoints: 0.0,
            loyaltyAdrRewards: false,
            loyaltyAdrRewardPoints: 0.0
        };
    }
    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);
}

function updateLoyaltyPoints(points, type = 'order') {
    if (type == 'adr') {
        setCartProperties({loyaltyAdrRewardPoints: parseFloat(points)}, true);
    } else {
        setCartProperties({loyaltyRewardPoints: parseFloat(points)}, true);
    }

    _getCart().updateCartLoyalty();
}

function loadLoyaltyPoints() {
    _getCart().updateCartLoyalty();
}

function toggleLoyaltyRewardPointsAdr() {
    let cart = _getCart();
    let cartUpdateObject = {};

    if (cart.loyaltyAdrRewards == true) {
        cartUpdateObject = {
            loyaltyAdrRewards: false,
            loyaltyAdrRewardPoints: 0.0};
    } else {
        cartUpdateObject = {
            loyaltyAdrRewards: true,
            loyaltyAdrRewardPoints: 0.0,
            loyaltyRewards: false,
            loyaltyRewardPoints: 0.0};
    }

    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);

}

async function getLoyaltyMaxRedeemAmount(isAdr) {
    let cart = _getCart();

    const totalRedeemPrice = isAdr ? cart.loyaltyTotalRedeemPrice : cart.loyaltyTotalRedeemPriceOrderItems;
    let loyaltyInfo = await webLoyalty.getRewardsPoints();

    return Math.min(
        loyaltyInfo.wallet.available,
        loyaltyInfo.userTier.redemptionLimit * totalRedeemPrice
    );
}

function calculateLoyaltyOrderTotal(useSapTotals) {
    let cart = _getCart();
    cart.updateCartLoyalty(cart);
    let cartInfo = getCartInfo();
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();

    const provideDefault = value => value ? value : 0.0

    if (loyaltyInfo.userTier.purchaseRatio == 0) {
        // if they cannot earn, just return zero
        return 0;
    } else if (useSapTotals) {
        // if we are using SAP totals, the loyalty reward points have already been deducted
        return provideDefault(cartInfo.loyaltySapTotalEarnPrice);
    } else if (cartInfo.loyaltyRewards) {
        // deduct standard point redemption

        const redeemOnly = provideDefault(parseFloat(cartInfo.loyaltyTotalRedeemPrice)) - provideDefault(parseFloat(cartInfo.loyaltyTotalEarnAndRedeemPrice)) // eslint-disable-line
        let earnEligibleRedemptionTotal = provideDefault(cartInfo.loyaltyRewardPoints) - redeemOnly
        if (earnEligibleRedemptionTotal < 0) {
            earnEligibleRedemptionTotal = 0
        }

        let orderTotal = provideDefault(cartInfo.loyaltyTotalEarnPrice) - earnEligibleRedemptionTotal;
        return orderTotal;
    } else if (cartInfo.loyaltyAdrRewards) {
        // this is an ADR-only order so deduct ADR point redemption
        const redeemOnly = provideDefault(parseFloat(cartInfo.loyaltyTotalRedeemPrice)) - provideDefault(parseFloat(cartInfo.loyaltyTotalAdrEarnAndRedeemPrice)) // eslint-disable-line
        let earnEligibleRedemptionTotal = provideDefault(cartInfo.loyaltyAdrRewardPoints) - redeemOnly
        if (earnEligibleRedemptionTotal < 0) {
            earnEligibleRedemptionTotal = 0
        }
        return provideDefault(cartInfo.loyaltyTotalEarnPrice) - earnEligibleRedemptionTotal;
    } else {
        return provideDefault(parseFloat(cartInfo.loyaltyTotalEarnPrice));
    }
}

function calculateLoyaltyEarningsToNextIncrement () {
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();
    let amountOverIncrement = 0.0;
    let amountToNextIncrement = 0.0;

    // Make sure they have an increment
    if (loyaltyInfo.userTier.purchaseIncrement == 0) {
        return 0.0;
    }

    let calcAdjustedLoyaltyTotal = calculateLoyaltyOrderTotal();

    amountOverIncrement = (calcAdjustedLoyaltyTotal % loyaltyInfo.userTier.purchaseIncrement);

    amountToNextIncrement = loyaltyInfo.userTier.purchaseIncrement - amountOverIncrement;

    return amountToNextIncrement;
}

function calculateLoyaltyEarnings (orderTotal = 0, useSapTotals = false) {
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();

    if (!loyaltyInfo || !loyaltyInfo.userTier) {
        return 0;
    }

    let currentOrderEarnings = 0.0;
    let calcAdjustedLoyaltyTotal = 0.0;
    let amountOverIncrement = 0.0;

    if (loyaltyInfo.userTier.purchaseRatio == 0) {
        return 0;
    }
    if (orderTotal != 0) {
        calcAdjustedLoyaltyTotal = orderTotal;
    } else {
        calcAdjustedLoyaltyTotal = calculateLoyaltyOrderTotal(useSapTotals);
    }

    // Check for a minimum amount to spend to get points
    if (loyaltyInfo.userTier.minSpend > 0) {

        // If the total doesn't exceed the min spend just return 0
        if (calcAdjustedLoyaltyTotal < loyaltyInfo.userTier.minSpend) {
            return 0;
        } else {
            // There is a min spend so calculate that portion of the rewards
            currentOrderEarnings += loyaltyInfo.userTier.minSpend * loyaltyInfo.userTier.purchaseRatio;
            // Remove min spend from order total
            calcAdjustedLoyaltyTotal = calcAdjustedLoyaltyTotal - loyaltyInfo.userTier.minSpend;
        }
    }

    // If there is a purchase increment calculate the totals
    if (loyaltyInfo.userTier.purchaseIncrement > 0) {

        amountOverIncrement = (calcAdjustedLoyaltyTotal % loyaltyInfo.userTier.purchaseIncrement);
        calcAdjustedLoyaltyTotal = calcAdjustedLoyaltyTotal - amountOverIncrement;

    }

    // Add the calculated total of point values
    currentOrderEarnings += calcAdjustedLoyaltyTotal * loyaltyInfo.userTier.purchaseRatio;

    const pointsMultiplier = getCachedConfigField('pointsMultiplier');
    if (pointsMultiplier && !isNaN(pointsMultiplier) && pointsMultiplier != "" && pointsMultiplier != 0) {

        currentOrderEarnings = currentOrderEarnings * pointsMultiplier;
    }

    if (loyaltyInfo.user.tier && loyaltyInfo.user.tier.maximumLimitPoints &&
        loyaltyInfo.user.tier.maximumLimitPoints > 0){
        // calculate max available to earn in period
        // Dealing with negative numbers and null values being passed by Annex
        const maxTierCapEarn = loyaltyInfo.user.tier.maximumLimitPoints -
            (!loyaltyInfo.user.tier.completedLimitPoints || loyaltyInfo.user.tier.completedLimitPoints < 0
                ? 0 : loyaltyInfo.user.tier.completedLimitPoints);
        if (currentOrderEarnings > maxTierCapEarn) {
            currentOrderEarnings = maxTierCapEarn;
        }
    }

    // Return amout that is earned on this total
    const country = RunConfigService.getRunConfig().country;
    const accuracy = CurrencyService.getCurrency(country).accuracy;
    return new Decimal(parseFloat(currentOrderEarnings)).toFixed(accuracy ? parseInt(accuracy) : 2);
}

function getOrderLoyaltyRewardsPointsJson() {
    let cart = getCartInfo();
    let loyaltyPoints = [];
    let runConfig = RunConfigService.getRunConfig();
    let country = runConfig.country;
    let currencyCode = CurrencyService.getCurrency(country).currencyCode;

    if (cart.loyaltyRewards == true) {
        loyaltyPoints =
            [
                {
                    'Type': 'POINTS_USED',
                    'Rate': cart.loyaltyRewardPoints,
                    'Currency': currencyCode
                }
            ];
    } else if (cart.loyaltyAdrRewards == true) {
        loyaltyPoints =
            [
                {
                    'Type': 'POINTS_USED',
                    'Rate': cart.loyaltyAdrRewardPoints,
                    'Currency': currencyCode
                }
            ];
    }

    return loyaltyPoints;
}

function containsAdrItems() {
    return _getCart().containsAdrItems();
}

function gotoPage(page) {
    let count = 0;
    events.publish('pause-persistent-cart');
    if (storage.getItem('pcActivity')) {
        let timerId = null;
        timerId = setInterval(() => {
            if (!storage.getItem('pcActivity') || count >= 200) {
                storage.removeItem('pcActivity');
                clearInterval(timerId);
                window.location = page;
            }
            count += 1;
        }, 20);
    } else {
        window.location = page;
    }
}

function goToCartPage(params = {}) {
    const config = getCachedConfiguration('Equinox_Markets');
    const country = RunConfigService.getRunConfig().country;

    if (
        equinoxLocalStorage.isEquinoxEnabled()
        && config.active
        && config.country_code === country
    ) {
        equinoxCartService.goToCartPage();
    } else {
        gotoPage(getCartPage(params));
    }
}

function checkContext(params){
    const context = localStorage.getItem('shoppingContext') ? JSON.parse(localStorage.getItem('shoppingContext')).context : null;
    if (context === 'skin_consult'){
        return params.isMySite
    } else {
        return typeof params.isMySite === 'boolean' ? params.isMySite : window.location.href.search(/\bmysite\b/) >= 0;
    }
}

function getCartPage(params = {}) {
    const queryParams = typeof params.queryParams === 'object' ? UrlService.toQueryString(params.queryParams) : '';
    const hashParams = params.hashParams ? `#${params.hashParams}` : '';
    const mySitePart = checkContext(params) ? '/mysite' : '';
    const page = `/static/cart/cart.html${queryParams ? '?' + queryParams : ''}#/${UrlService.getLocale()}${mySitePart}/cart${hashParams}`;

    return page;
}

async function checkIsMySiteSiteEnable (){
    let runConfig = RunConfigService.getRunConfig(null);
    let storefrontMarketsUrl = `${runConfig.baseUrl}/content/nuskin/storefront-redirect/jcr:content/storefrontMarkets.json`;
    let response = await axios.get(storefrontMarketsUrl, { headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } });
    let mySiteEnableConfig = response.data.storefrontMarkets.indexOf(UrlService.getLocale()) >= 0;
    return mySiteEnableConfig
}
function goToCheckoutPage(params = {route: 'orderSummary'}) {
    gotoPage(getCheckoutPage(params));
}

function getCheckoutPage(params = {}) {
    return `/static/checkout/checkout.html#/${UrlService.getLocale()}${checkContext(params) ? '/mysite' : ''}/${params.route}`;
}

function syncPersistedInfo(pLineInfo) {
    if (pLineInfo) {
        const cart = _getCart();

        cart.syncPersistedLineInfo(pLineInfo);
        setCart(cart, false);
    }
}

// listener setup. Waits for the following config to be loaded.
const neededConfigMaps = ['Url', 'Basic', 'Shopping', 'ADP', 'Cart', 'Loyalty', 'Checkout', 'Equinox_Markets'];

const isSubset = (array1, array2) => {
    return array2.every((element) => array1.includes(element));
}

const cartServiceConfigCallback = async (configs) => {
    if (configs != null && isSubset(configs, neededConfigMaps)) {
        events.unsubscribe(events.global.CS_CONFIG_SET, cartServiceConfigCallback);

        if (nuskin && nuskin.account) {
            events.subscribe(events.authentication.LOGOUT, function () {
                clearCart(false);
                if (typeof AdrEditService !== 'undefined') {
                    AdrEditService.clearAdrEdit();
                }
            });
            events.subscribe(events.authentication.LOGIN, function() {
                let user = UserService.getUser();
                if (user) {
                    let cart = _getCart();
                    cart.userId = user.id;
                    setCart(cart);
                }
            });
            events.getValue(events.shop.PERSONAL_OFFER_LOADED, function(personalOffer) {
                if (personalOffer) {
                    let cart = _getCart();

                    if (!cart.isPitchCart) {
                        cart = clearCart();
                    }
                    cart.isPitchCart = true;
                    setCart(cart);
                }
            }, true);
            events.getValue(events.shop.CART_SYNCED_ON_LOAD, function(synced) {
                if (synced) {
                    cartSynced = true;
                    setCartString();
                }
            }, true);
        }

        events.getValue(events.salesevent.ACTIVE_EVENTS, (_activeEvents) => {
            if (_activeEvents != undefined) {
                activeEvents = _activeEvents;
                const cart = _getCart();
                cart.updateEventPrices(activeEvents)
                setCart(cart);
            }
        }, true);
        events.publish(events.salesevent.POLL_MARKET_CONFIG);
    }
}

/**
 * Waits for config to be loaded and then setts up the listeners
 */
events.getValue(events.global.CS_CONFIG_SET, cartServiceConfigCallback, true);
